/**
 * Jubly Calendar Month View — Google Calendar style
 * Clean grid with colored event chips
 */

import { useMemo } from 'react';

export default function MonthView({
  currentDate,
  events,
  calendars,
  onDateSelect,
  onEventClick,
  onCreateEvent
}) {
  const monthData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    const days = [];

    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonth.getDate() - i);
      days.push({ date: prevMonth.getDate() - i, currentMonth: false, fullDate: d, events: [] });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dayStart = new Date(date); dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date); dayEnd.setHours(23, 59, 59, 999);

      const dayEvents = events.filter(event => {
        const s = new Date(event.start_time);
        const e = new Date(event.end_time);
        return s <= dayEnd && e >= dayStart;
      }).sort((a, b) => {
        if (a.is_all_day && !b.is_all_day) return -1;
        if (!a.is_all_day && b.is_all_day) return 1;
        return new Date(a.start_time) - new Date(b.start_time);
      });

      days.push({ date: i, currentMonth: true, fullDate: date, events: dayEvents });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: i, currentMonth: false, fullDate: new Date(year, month + 1, i), events: [] });
    }
    return days;
  }, [currentDate, events]);

  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const today = new Date();
  const isToday = (date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? 'p' : 'a';
    const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m > 0 ? `${hour}:${m.toString().padStart(2, '0')}${ampm}` : `${hour}${ampm}`;
  };

  const getEventColor = (event) => event.calendar_color || event.color || '#3B82F6';

  const MAX_VISIBLE = 3;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {dayNames.map(day => (
          <div key={day} className="py-2 text-center text-[11px] font-medium text-gray-500 tracking-wider">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="flex-1 grid grid-cols-7 auto-rows-fr">
        {monthData.map((day, idx) => (
          <div
            key={idx}
            className={`
              border-b border-r border-gray-100 flex flex-col min-h-0 overflow-hidden
              ${!day.currentMonth ? 'bg-gray-50/50' : ''}
              cursor-pointer
            `}
            onClick={() => onDateSelect?.(day.fullDate)}
          >
            {/* Day number */}
            <div className="flex justify-center pt-1.5 pb-0.5">
              <span className={`
                text-xs w-6 h-6 flex items-center justify-center rounded-full
                ${isToday(day.fullDate) ? 'bg-blue-600 text-white font-bold' : ''}
                ${!day.currentMonth ? 'text-gray-400' : 'text-gray-700'}
              `}>
                {day.date}
              </span>
            </div>

            {/* Event chips */}
            <div className="flex-1 px-1 space-y-px overflow-hidden">
              {day.events.slice(0, MAX_VISIBLE).map(event => {
                const color = getEventColor(event);
                if (event.is_all_day) {
                  // All-day: full-color pill
                  return (
                    <button
                      key={event.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(event); }}
                      className="w-full text-left text-[11px] px-1.5 py-px rounded font-medium text-white truncate block hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: color }}
                    >
                      {event.title}
                    </button>
                  );
                }
                // Timed: dot + time + title
                return (
                  <button
                    key={event.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick?.(event); }}
                    className="w-full text-left text-[11px] px-1 py-px rounded flex items-center gap-1 truncate hover:bg-gray-100 transition-colors"
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-gray-500 flex-shrink-0">{formatTime(event.start_time)}</span>
                    <span className="text-gray-800 truncate">{event.title}</span>
                  </button>
                );
              })}
              {day.events.length > MAX_VISIBLE && (
                <button
                  onClick={(e) => { e.stopPropagation(); }}
                  className="text-[11px] text-gray-500 font-medium px-1 hover:text-blue-600 transition-colors"
                >
                  +{day.events.length - MAX_VISIBLE} more
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
